import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  MIN_POLL_MS,
  MAX_ATTEMPTS,
  TICK_MS,
  clamp,
  nextInterval,
  secondsLeft,
} from './deviceCodePolling';
import {
  TenantAppCredential,
  TenantAppPurpose,
  TenantAppSession,
  TenantDetail,
  beginTenantAppSession,
  createTenantApplication,
  removeTenantApplicationPreviousKey,
  rotateTenantApplication,
  tenantAppErrorCode,
  tenantAppRetryAfterSeconds,
} from '../api/tenantRegistry';

// FD-530 / FD-531 — the brokered tenant-application sign-in (`FD-ADR-046` §3, §6).
//
// One state machine, three actions, because all three are the same two calls with
// a different verb at the end: begin a device authorization grant against the
// CUSTOMER'S tenant, then poll a redeem-and-act endpoint until a human finishes
// signing in. Splitting them into three hooks would be three copies of the timing
// discipline below, and the thing that discipline prevents is creating a second
// application in someone else's directory.
//
// WHY A SIGN-IN AT ALL, since it is the cost every screen in this flow pays:
// `FD-ADR-046` D1 REFUSED `Application.ReadWrite.OwnedBy`, so AirBridge holds no
// standing authority over an application object in a customer's tenant. Nothing
// here can run unattended — by construction, not by policy.
//
// The Graph access token is redeemed and consumed ENTIRELY server-side. What
// crosses the wire to this hook is a `deviceCode`: a single-use, ~15-minute GRANT
// HANDLE that is worthless until the paired user code is entered by an
// Application Administrator of that tenant.
//
// The poll is a settle-then-schedule chain and never an interval — see
// `deviceCodePolling.ts`, which carries that reasoning and the two failure modes
// it exists to prevent.

/** Which brokered action a session is for. */
export type TenantAppAction = 'create' | 'rotate' | 'remove-previous-key';

/**
 * The `purpose` the SESSION endpoint is started with, which is not the same thing
 * as the action.
 *
 * The session endpoint checks exactly one precondition and it INVERTS between the
 * two purposes: `create` refuses a tenant that already has an application on file,
 * `rotate` refuses one that does not. Removing a superseded key is a rotation-shaped
 * act on an application that must already exist, so it takes `rotate` — sending
 * `create` would have the session refuse every tenant it is ever legitimately used on.
 */
function purposeFor(action: TenantAppAction): TenantAppPurpose {
  return action === 'create' ? 'create' : 'rotate';
}

/** What the redeem arm returns, narrowed to what every caller here needs. */
export interface TenantAppActionResult {
  action: TenantAppAction;
  /** The refreshed tenant view the server returned alongside the write. */
  tenant: TenantDetail;
  /** Where consent is granted — `create` only, `null` for the rotation actions. */
  consentUrl: string | null;
  /** R-FD-055: the console-returning grant link, when the application has one. */
  grantUrl: string | null;
  /** Present on `create`; the application that now exists in the customer's tenant. */
  clientId: string | null;
}

export type TenantAppPhase =
  | { kind: 'idle' }
  /** The session call is in flight. */
  | { kind: 'starting'; action: TenantAppAction }
  /** The admin has the code and has not finished signing in. */
  | { kind: 'awaiting-signin'; action: TenantAppAction; session: TenantAppSession; secondsRemaining: number }
  /**
   * An attempt has outrun a pending check — this one is doing the work.
   *
   * `session` is ABSENT on the pasted-token arm (FD-551), which has no device code
   * and no poll: the operator submits a Graph token they obtained themselves and the
   * single call either does the work or fails. The dialog must therefore not assume
   * a session here — it renders the code block only when one exists.
   */
  | { kind: 'working'; action: TenantAppAction; session?: TenantAppSession }
  | { kind: 'done'; action: TenantAppAction; result: TenantAppActionResult }
  /** The sign-in window closed. Nothing was written; nothing to clean up. */
  | { kind: 'expired'; action: TenantAppAction }
  /**
   * The server refused because the thing being removed was already gone, and
   * corrected its own record while refusing. Not a failure, not a success — see
   * the branch that sets it.
   */
  | { kind: 'already-resolved'; action: TenantAppAction }
  | { kind: 'failed'; action: TenantAppAction; error: unknown };

export interface TenantAppSignIn {
  phase: TenantAppPhase;
  /** Begins a session. Called from a click, never an effect. */
  start: (action: TenantAppAction) => void;
  /**
   * The pasted-token arm (FD-551). ONE call, no session, no poll — the operator has
   * already completed a sign-in elsewhere, so there is nothing to wait for.
   *
   * It exists because THE GUIDED PATH IS BLOCKABLE IN SOMEONE ELSE'S TENANT.
   * Microsoft recommends blocking the device-code flow, and a Conditional Access
   * authentication-flows policy will. `FD-ADR-032` §6 kept the paste path first-class
   * in the wave dialog for exactly that reason; the server has accepted
   * `graphAccessToken` on all three of these endpoints since FD-527 and only the
   * console was missing an arm.
   */
  submitToken: (action: TenantAppAction, graphAccessToken: string) => void;
  /** Abandons the session. Makes NO server call — a device code has no revoke. */
  cancel: () => void;
  /**
   * Set when the SERVER says the guided path is off for this deployment, so the
   * dialog can HIDE it rather than offer a route that fails after the operator has
   * committed to it — the `DRS_DEVICE_CODE_ENABLED` discipline of `FD-ADR-032` §6.
   */
  guidedDisabled: boolean;
}

/**
 * How long an in-flight attempt must run before it is reported as working rather
 * than waiting. A pending 409 is one token POST and returns fast; the successful
 * attempt generates a vault certificate and writes to Graph. Crossing this line is
 * the only signal available that the sign-in landed, and without it the dialog
 * looks stalled at the exact moment it is doing the irreversible part.
 */
export const WORKING_HINT_MS = 10_000;

export function useTenantAppSignIn(tenantId: string, onDone?: () => void): TenantAppSignIn {
  const [phase, setPhase] = useState<TenantAppPhase>({ kind: 'idle' });
  // Latched, never cleared: once the server has said the guided path is off for this
  // deployment, it stays off for the life of the dialog. Clearing it on the next
  // render would re-offer the route that just failed.
  const [guidedDisabled, setGuidedDisabled] = useState(false);
  const queryClient = useQueryClient();

  const pollTimer = useRef<number | undefined>(undefined);
  const tickTimer = useRef<number | undefined>(undefined);
  const intervalMs = useRef(MIN_POLL_MS);
  const attempts = useRef(0);
  const deadlineMs = useRef(0);
  const running = useRef(false);
  const inFlightSince = useRef(0);

  // Read through a ref rather than captured: the callback is armed inside a timer
  // that outlives the render it was created in, so a parent re-render mid-poll
  // would otherwise invoke a stale closure.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const stop = useCallback(() => {
    running.current = false;
    if (pollTimer.current !== undefined) window.clearTimeout(pollTimer.current);
    if (tickTimer.current !== undefined) window.clearInterval(tickTimer.current);
    pollTimer.current = undefined;
    tickTimer.current = undefined;
    inFlightSince.current = 0;
  }, []);

  // Unmount stops the chain. Without this a closed dialog keeps creating
  // applications in a customer's tenant in the background — every poll is a
  // mutation, not a read.
  useEffect(() => stop, [stop]);

  const succeed = useCallback(
    (action: TenantAppAction, result: TenantAppActionResult) => {
      stop();
      setPhase({ kind: 'done', action, result });
      // Prime rather than invalidate: the server already returned the re-read view
      // alongside the write, so seeding it updates every surface on this page in the
      // same paint. Refetching would let the action's own result and the page behind
      // it disagree for a round trip.
      queryClient.setQueryData(['tenant', tenantId], result.tenant);
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      onDoneRef.current?.();
    },
    [queryClient, stop, tenantId],
  );

  const redeem = useCallback(
    async (
      action: TenantAppAction,
      credential: TenantAppCredential,
    ): Promise<TenantAppActionResult> => {
      if (action === 'create') {
        // R-FD-055: send this console's origin so the new application can return
        // "Grant permissions" here. The server ignores an origin it does not serve.
        const r = await createTenantApplication(tenantId, credential, window.location.origin);
        return {
          action,
          tenant: r.tenant,
          consentUrl: r.consentUrl,
          grantUrl: r.grantUrl ?? null,
          clientId: r.clientId,
        };
      }
      if (action === 'rotate') {
        const r = await rotateTenantApplication(tenantId, credential);
        return { action, tenant: r.tenant, consentUrl: null, grantUrl: null, clientId: r.clientId };
      }
      const r = await removeTenantApplicationPreviousKey(tenantId, credential);
      return { action, tenant: r.tenant, consentUrl: null, grantUrl: null, clientId: r.clientId };
    },
    [tenantId],
  );

  const poll = useCallback(
    async (action: TenantAppAction, session: TenantAppSession) => {
      if (!running.current) return;

      // The deadline is Microsoft's, and there is nothing to tell the Manager when
      // it passes — so expiry stops with ZERO further calls.
      if (Date.now() >= deadlineMs.current || attempts.current >= MAX_ATTEMPTS) {
        stop();
        setPhase({ kind: 'expired', action });
        return;
      }

      attempts.current += 1;
      inFlightSince.current = Date.now();

      try {
        const result = await redeem(action, { deviceCode: session.deviceCode });
        if (!running.current) return;
        succeed(action, result);
        return;
      } catch (err) {
        if (!running.current) return;
        inFlightSince.current = 0;

        const code = tenantAppErrorCode(err);

        if (code === 'AUTHORIZATION_PENDING') {
          intervalMs.current = nextInterval(intervalMs.current, tenantAppRetryAfterSeconds(err));
          setPhase({
            kind: 'awaiting-signin',
            action,
            session,
            secondsRemaining: secondsLeft(deadlineMs.current),
          });
          pollTimer.current = window.setTimeout(
            () => void poll(action, session),
            intervalMs.current,
          );
          return;
        }

        // `TENANT_APP_SUPERSEDED_KEY_ABSENT` is a 409, and it is neither a failure
        // nor a success — which is exactly why it gets its own phase rather than
        // being folded into one of them.
        //
        // The key the operator asked to remove is already gone, so the OUTCOME they
        // wanted holds; and the server clears the stale pointer as it refuses, so
        // the page behind this dialog is now correct where a moment ago it was not.
        // Reporting it as a failure sends someone looking for a problem that has
        // resolved itself. Reporting it as a success claims this request removed
        // something, which it did not. Saying what actually happened costs one
        // phase and is the only version that is true.
        if (code === 'TENANT_APP_SUPERSEDED_KEY_ABSENT') {
          stop();
          setPhase({ kind: 'already-resolved', action });
          void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
          onDoneRef.current?.();
          return;
        }

        stop();
        setPhase({ kind: 'failed', action, error: err });
      }
    },
    [queryClient, redeem, stop, succeed, tenantId],
  );

  const start = useCallback(
    (action: TenantAppAction) => {
      // Idempotent against a double click and against StrictMode. `start` is only
      // ever called from a click handler, never from an effect.
      if (running.current) return;
      running.current = true;
      attempts.current = 0;
      intervalMs.current = MIN_POLL_MS;
      setPhase({ kind: 'starting', action });

      void (async () => {
        let session: TenantAppSession;
        try {
          session = await beginTenantAppSession(tenantId, purposeFor(action));
        } catch (err) {
          if (!running.current) return;
          stop();
          // The deployment has the guided path switched off, or has no provisioning
          // client id to run it against. Both are configuration, not a fault, and
          // both are permanent for this page — so latch it and let the dialog hide
          // the arm instead of leaving the operator to retry a route that cannot work.
          const code = tenantAppErrorCode(err);
          if (code === 'GRAPH_DEVICE_CODE_DISABLED' || code === 'GRAPH_DEVICE_CODE_NOT_CONFIGURED') {
            setGuidedDisabled(true);
          }
          setPhase({ kind: 'failed', action, error: err });
          return;
        }
        if (!running.current) return;

        deadlineMs.current = Date.parse(session.expiresUtc);
        if (!Number.isFinite(deadlineMs.current)) {
          // A malformed expiry must not become an unbounded poll. Fall back to the
          // relative window the same response carries.
          deadlineMs.current = Date.now() + Math.max(0, session.expiresInSeconds) * 1_000;
        }
        intervalMs.current = clamp(session.intervalSeconds * 1_000);

        setPhase({
          kind: 'awaiting-signin',
          action,
          session,
          secondsRemaining: secondsLeft(deadlineMs.current),
        });

        tickTimer.current = window.setInterval(() => {
          const remaining = secondsLeft(deadlineMs.current);
          setPhase((current) => {
            if (current.kind === 'awaiting-signin') {
              if (
                inFlightSince.current > 0 &&
                Date.now() - inFlightSince.current >= WORKING_HINT_MS
              ) {
                return { kind: 'working', action: current.action, session: current.session };
              }
              return { ...current, secondsRemaining: remaining };
            }
            return current;
          });
          // Expiry is enforced here as well as in `poll`, because the gap between
          // two polls is up to 60s and the countdown must not sit at zero while a
          // stale timeout waits to fire.
          if (remaining <= 0 && inFlightSince.current === 0) {
            stop();
            setPhase({ kind: 'expired', action });
          }
        }, TICK_MS);

        pollTimer.current = window.setTimeout(() => void poll(action, session), intervalMs.current);
      })();
    },
    [poll, stop, tenantId],
  );

  const submitToken = useCallback(
    (action: TenantAppAction, graphAccessToken: string) => {
      // Same double-click guard as `start`, and it matters more here: each call is a
      // WRITE in a customer's directory, and `create` run twice is two application
      // registrations AirBridge has no standing authority to remove (`FD-ADR-046` D1).
      if (running.current) return;
      const token = graphAccessToken.trim();
      if (!token) return;

      running.current = true;
      // No session: nothing to poll, nothing to count down, nothing to expire. The
      // operator has already signed in wherever they obtained the token.
      setPhase({ kind: 'working', action });

      void (async () => {
        try {
          const result = await redeem(action, { graphAccessToken: token });
          if (!running.current) return;
          succeed(action, result);
        } catch (err) {
          if (!running.current) return;
          stop();

          // The same 409 that is neither failure nor success on the guided arm. It is
          // a property of the REQUEST, not of how the credential was obtained, so it
          // must be handled identically here or the two arms report the same server
          // answer differently.
          if (tenantAppErrorCode(err) === 'TENANT_APP_SUPERSEDED_KEY_ABSENT') {
            setPhase({ kind: 'already-resolved', action });
            void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
            onDoneRef.current?.();
            return;
          }

          setPhase({ kind: 'failed', action, error: err });
        }
      })();
    },
    [queryClient, redeem, stop, succeed, tenantId],
  );

  const cancel = useCallback(() => {
    // NO server call. A device code has no revoke — it simply expires, and an
    // abandoned one leaves nothing behind in the customer's tenant.
    stop();
    setPhase({ kind: 'idle' });
  }, [stop]);

  return { phase, start, submitToken, cancel, guidedDisabled };
}
